import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import orderBy from 'lodash/orderBy';
import Logger from '../../../../../../ui-kitchen/src/utils/logger';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import AmexFranchiseEditorialsCard from './AmexFranchiseEditorialsCard';
import { getEditorialData } from '../AmexUtils';
import './AmexFranchiseEditorials.scss';

const AmexFranchiseEditorials = (
  {
    Analytics,
    baseRequestUrl,
    editorialsConfig: {
      section_id: sectionId,
      section_title: sectionTitle,
      section_intro: sectionIntro,
      is_api_feed: isApiFeed,
      layout: sectionLayout,
      max_cards: maxCards,
      utm,
    },
    editorialsImagePath,
    blogDataArray,
    gdaEditorialApi,
    editorialsData = {},
    selectedCityCode,
    matchMedia,
    taggingConfig,
    componentCallback,
  }
) => {
  const [editorialsList, setEditorialsList] = useState([]);

  const validateEditorialsContent = (editorialsListData) => {
    const requiredKeys = ['image', 'title', 'url', 'spot'];
    const imageKeys1x1 = ['desktop', 'mobile'];
    const imageKeys1x2 = ['desktop_large', 'desktop_small', 'mobile'];
    const requiredImageKeys = sectionLayout === '1x1' ? imageKeys1x1 : imageKeys1x2;

    if (!editorialsListData || editorialsListData.length === 0) return [];
    return editorialsListData
      .filter((item) => requiredKeys.every((i) => Object.prototype.hasOwnProperty.call(item, i) && item[i]))
      .reduce((acc, d) => {
        const { image } = d;
        const hasImage = requiredImageKeys
          .every((i) => Object.prototype.hasOwnProperty.call((image), i) && image[i]);
        if (hasImage) acc.push({ ...d, hasImage });
        return acc;
      }, []);
  };

  const maxCardsToShow = maxCards || 2;
  const reorderBlogDataArray = (blogDataResponseList) => {
    const validatedBlogList = validateEditorialsContent(blogDataResponseList);
    const finalEditorialList = orderBy(validatedBlogList, ['position'], ['asc']);
    setEditorialsList(finalEditorialList.slice(0, maxCardsToShow));
  };

  useEffect(() => {
    const fetchApiEditorials = async () => {
      try {
        const results = await getEditorialData(blogDataArray, gdaEditorialApi, sectionLayout);
        if (results && results.length > 0) reorderBlogDataArray(results);
      } catch (error) {
        Logger.error(error);
      }
    };
    // fetch api editorials
    if (isApiFeed) fetchApiEditorials();
    else {
      // fetch static editorials
      const filteredEditorials = validateEditorialsContent(editorialsData[selectedCityCode]);
      setEditorialsList(filteredEditorials.slice(0, maxCardsToShow));
    }
    componentCallback('isEditorialsDataLoaded', (editorialsList && editorialsList.length === 0));
  }, [blogDataArray, editorialsData]);

  if (editorialsList && editorialsList.length === 0) return null;

  const sanitizedSectionTitle = sanitizeString(sectionTitle);
  const sanitizedSectionIntro = sanitizeString(sectionIntro);

  return (
    <section className="AmexFranchiseEditorials">
      <div className="container">
        {sectionTitle && (
          <h2
            id={sectionId}
            className="AmexFranchiseEditorials__title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedSectionTitle,
            }}
          />
        )}
        {sectionIntro && (
          <p
            className="AmexFranchiseEditorials__intro"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedSectionIntro,
            }}
          />
        )}
        <ul className={`AmexFranchiseEditorials__grid layout__${sectionLayout}`}>
          {editorialsList.map((editorial) => (
            <li
              key={editorial.title}
              className={`spot-${editorial.spot}`}
            >
              <AmexFranchiseEditorialsCard
                Analytics={Analytics}
                baseRequestUrl={baseRequestUrl}
                editorial={editorial}
                editorialsImagePath={editorialsImagePath}
                isApiFeed={isApiFeed}
                matchMedia={matchMedia}
                utm={utm}
                taggingConfig={taggingConfig}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

AmexFranchiseEditorials.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  editorialsConfig: PropTypes.shape({
    section_id: PropTypes.string.isRequired,
    section_title: PropTypes.string.isRequired,
    section_intro: PropTypes.string.isRequired,
    is_api_feed: PropTypes.bool,
    layout: PropTypes.string.isRequired,
    max_cards: PropTypes.number,
    utm: PropTypes.string.isRequired,
  }).isRequired,
  blogDataArray: PropTypes.arrayOf(PropTypes.shape({})),
  editorialsData: PropTypes.shape({}),
  editorialsImagePath: PropTypes.string,
  gdaEditorialApi: PropTypes.arrayOf(PropTypes.shape({})),
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  selectedCityCode: PropTypes.string,
  taggingConfig: PropTypes.shape({}),
  componentCallback: PropTypes.func,
};

AmexFranchiseEditorials.defaultProps = {
  taggingConfig: undefined,
  componentCallback: () => {},
  selectedCityCode: null,
  blogDataArray: [],
  editorialsData: {},
  gdaEditorialApi: [],
  editorialsImagePath: null,
};

export default AmexFranchiseEditorials;
